import { useMediaQuery } from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { LoadingContext, M_StateActions, MTabsInpage, NavigateContext } from 'src/M_Components';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import TenderInQuotation from '../Tenders/TenderInQuotation';
import TenderFinished from '../Tenders/TenderFinished';
import SpotTenderConfig from './SpotTenderConfig';
import { hasPermission } from 'src/components/utils/permissionsHelper';

// unified tabs styling 
import 'src/pages/tabsInpage.css';

export default function SpotTenderPage({ roles, onCreateTab, toastMessages, handleTabClose }) {
    const ctx = useContext(LoadingContext);
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const matches = useMediaQuery('(min-width:900px)');
    const M_State = useSelector((state) => state.StateKeyValue);
    const location = useLocation();
    const { renameTab } = useContext(NavigateContext);
    const selectedTab = useSelector(s => s.StateKeyValue.find(o => o.key === 'selectedTab')?.value);

    // Opens the tab with the last selected menu item
    // If no session data is found, it defaults to the first tab (menu 1)
    const [menu, setMenu] = useState(1);
    const [refresh, setRefresh] = useState(false);

    const [id, setId] = useState(() => M_State.find(obj => obj.key === 'selectedTab')?.value);

    const sessionKeys = ['menu', 'refresh']; // Keys to store in session storage

    //============================================================================================================
    // EFFECT
    // Rename tab on language change
    //============================================================================================================
    useEffect(() => {
        if (!selectedTab) return; // If no tab is selected, do nothing
        renameTab(String(selectedTab), t('SpotTenders'));
    }, [t, selectedTab]);
 
    //============================================================================================================
    // Upsert session data for the current tab
    //============================================================================================================
    const upsertSession = (tabId, kv) => {
        const current = M_State.find(o => o.key === tabId)?.value || []; // Get the current session data
        const filtered = current.filter(e => e.key !== kv.key); // Remove any existing entry for the key
        dispatch(M_StateActions.addM_State({ key: tabId, value: [...filtered, kv] })); // Update the session data
    };

    //============================================================================================================
    // Function to retrieve session storage data based on session ID
    //============================================================================================================
    const getSessionStorageData = (sessionID) => {
        ctx.setIsLoad(true);
        const session = M_State.find(obj => obj.key === sessionID)?.value; // Retrieve session data from M_State
        if (session) {  // If session data is found
            const m = session.find(x => x.key === 'menu')?.value; // Get the menu value from session data
            const ref = session.find(x => x.key === 'refresh')?.value; // Get the refresh value from session data
            setMenu(typeof m === 'number' ? m : 1); // Set the menu state to the retrieved value or default to 1
            if (ref !== undefined) setRefresh(ref); // Set the refresh state to the retrieved value if it exists
        } else {
            setMenu(1); // Default to menu 1 if no session data is found
        }
        ctx.setIsLoad(false);
    };

    //============================================================================================================
    // EFFECT
    // Retrieve session data when the component mounts or when the pathname changes
    //============================================================================================================
    useEffect(() => {
        getSessionStorageData(id); // Call the function to get session data
        setRefresh(r => !r); // Toggle refresh state to trigger re-render
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    //============================================================================================================
    // EFFECT
    // useEffect for selectedTab changes
    //============================================================================================================
    useEffect(() => {
        const newId = M_State.find(obj => obj.key === 'selectedTab')?.value; // Get the new selected tab ID
        if (newId && newId !== id) { // If the selected tab ID has changed
            setId(newId); // Update the ID state
            getSessionStorageData(newId); // Retrieve session data for the new tab
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [M_State]);

    //============================================================================================================
    // EFFECT
    // Function to handle tab close
    //============================================================================================================
    useEffect(() => {
        if (!id) return;
        // Validate session data before upserting
        upsertSession(id, { key: 'menu', value: menu });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, menu]);

    //============================================================================================================
    // EFFECT
    // Function to handle tab close
    //============================================================================================================
    useEffect(() => {
        if (!id) return;
        upsertSession(id, { key: 'refresh', value: refresh });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, refresh]);

    //============================================================================================================
    // Function to determine which tabs to display based on user permissions
    //============================================================================================================
    const allowedTabs = () => {
        const tabArray = [
            { id: 1, labelTab: t('SpotTendersConfiguration'), iconTab: <i className="pi pi-wrench"></i> },
        ];

        // If user has permissions to update Tenders Module, render tabs with ids 2 and 3
        if (hasPermission(roles, 'Tenders', 'Update') || hasPermission(roles, 'Tenders', 'Admin')) {
            tabArray.push(
                { id: 2, labelTab: t('SpotTendersInQuotation'), iconTab: <i className="pi pi-chart-line"></i> },
                { id: 3, labelTab: t('SpotTendersFinished'), iconTab: <i className="pi pi-check-square"></i> }
            );
        }

        return tabArray;
    };

    return (
        // Backward-compat bridge + unified layout classes (visual only)
        <div className="tabsInpage divGlobalSettings">
            <div className="tabsInpage__title titleSettings" style={{ color: ctx.secondaryColor }}>
                {t('SpotTenders')}
            </div>
            {/* Standard body split: sidebar (tabs) + content (panel) */}
            <div className="tabsInpage__body">
                {/* Sidebar column (controlled by CSS: widths, separator, responsive) */}
                <div className="tabsInpage__sidebar">
                    <MTabsInpage // Tab component for navigation
                        type={matches ? 'column' : 'tabs'}
                        renderTabs={allowedTabs()}
                        firstTabOpen={menu}
                        onGetTab={(obj) => setMenu(obj.id)}
                        fixedArea={false}
                    />
                </div>
                {/* Right content panel. Legacy class kept for bridge; no behavior changes */}
                <div className="tabsInpage__content settingsPageMainDiv">
                    {menu === 1 && (
                        <SpotTenderConfig // Configuration component for Spot Tenders
                            roles={roles}
                            onCreateTab={onCreateTab}
                            toastMessages={toastMessages}
                        />
                    )}
                    {menu === 2 && (
                        <TenderInQuotation // Component for Tenders in Quotation
                            tenderTypeId={1}               // SPOT
                            mode="spot"
                            roles={roles}
                            onCreateTab={onCreateTab}
                            toastMessages={toastMessages}
                        />
                    )}
                    {menu === 3 && (
                        <TenderFinished // Component for Finished Tenders
                            tenderTypeId={1}               // SPOT
                            mode="spot"
                            roles={roles}
                            onCreateTab={onCreateTab}
                            toastMessages={toastMessages}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}
